import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, Share2, Users, ChevronLeft } from 'lucide-react';
import api from '@/lib/api';
import ReferralShareModal from '@/components/referral/ReferralShareModal';
import ResqlyCard from '@/components/resqly/ResqlyCard';
import ResqlyEmptyState from '@/components/resqly/ResqlyEmptyState';
import { Button } from '@/components/ui/button';
import {
  buildReferralLink,
  copyText,
  formatReferralStatus,
} from '@/lib/referral';

export default function ProviderReferral() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [me, setMe] = useState(null);
  const [direct, setDirect] = useState([]);
  const [network, setNetwork] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [meRes, directRes, networkRes] = await Promise.all([
        api.get('/referrals/me'),
        api.get('/referrals/me/direct'),
        api.get('/referrals/me/network'),
      ]);
      setMe(meRes.data);
      setDirect(directRes.data.direct_referrals || []);
      setNetwork(networkRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load referral dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const code = me?.referral_code || '';
  const link = buildReferralLink(code);
  const stats = me?.stats || {};

  const onCopy = async () => {
    await copyText(code);
    toast.success('Code copied');
  };

  const onShare = () => setShareOpen(true);

  return (
    <div className="resqly-shell min-h-screen bg-[var(--resqly-surface-muted)] pb-8">
      <div className="resqly-frame">
        <header className="px-5 pt-5 pb-3 flex items-center gap-2">
          <button type="button" onClick={() => navigate('/provider/dashboard')} className="p-2 -ml-2 rounded-full hover:bg-slate-100">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900">Refer & Grow</h1>
        </header>

        <div className="px-5 space-y-4">
          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {error && (
            <ResqlyCard className="p-4">
              <p className="text-sm text-rose-600">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={load}>Retry</Button>
            </ResqlyCard>
          )}

          {!loading && !error && me && (
            <>
              <ResqlyCard className="p-5 space-y-3" elevated data-testid="ranger-referral-dashboard">
                <p className="text-sm text-slate-600">Your Ranger referral code</p>
                <p className="text-xl font-semibold text-[var(--resqly-primary)] break-all">{code}</p>
                <p className="text-xs text-slate-500">{me.financial_rewards_status}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={onCopy} data-testid="ranger-copy-code">
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                  <Button size="sm" onClick={onShare} data-testid="ranger-share">
                    <Share2 className="w-4 h-4 mr-1" /> Share
                  </Button>
                </div>
              </ResqlyCard>

              <div className="grid grid-cols-3 gap-2">
                <Stat label="Direct Rangers" value={stats.direct_count || 0} />
                <Stat label="Active" value={stats.active_count || 0} />
                <Stat label="Total network" value={stats.total_network || 0} />
              </div>

              <section>
                <h2 className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-1">
                  <Users className="w-4 h-4" /> Your network
                </h2>
                {direct.length === 0 ? (
                  <ResqlyEmptyState title="No invites yet" description="Share your code to grow your Ranger community." />
                ) : (
                  <div className="space-y-2">
                    {direct.map((row) => (
                      <ResqlyCard key={row.relationship_id} className="p-3 flex justify-between">
                        <div>
                          <p className="font-medium">{row.display_name}</p>
                          <p className="text-xs text-slate-500">Level {row.level}</p>
                        </div>
                        <span className="text-xs">{formatReferralStatus(row.status)}</span>
                      </ResqlyCard>
                    ))}
                  </div>
                )}
              </section>

              {network?.network?.length > 0 && (
                <section>
                  <h2 className="text-sm font-medium text-slate-800 mb-2">Extended network</h2>
                  <p className="text-xs text-slate-500 mb-2">Up to {network.levels} levels</p>
                  <NetworkTree nodes={network.network} />
                </section>
              )}
            </>
          )}
        </div>
      </div>
      <ReferralShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        code={code}
        shareMessage="Join RESQLY as a Ranger — trusted home healthcare."
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <ResqlyCard className="p-3 text-center">
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </ResqlyCard>
  );
}

function NetworkTree({ nodes, depth = 0 }) {
  if (!nodes?.length) return null;
  return (
    <ul className={`space-y-2 ${depth > 0 ? 'ml-4 border-l border-slate-200 pl-3' : ''}`}>
      {nodes.map((node) => (
        <li key={node.relationship_id || node.user_id}>
          <ResqlyCard className="p-2 text-sm">
            {node.display_name} · {formatReferralStatus(node.status)}
          </ResqlyCard>
          <NetworkTree nodes={node.children} depth={depth + 1} />
        </li>
      ))}
    </ul>
  );
}
